import type {OutlookRecipient} from './EmailScreen/Content/EmailRecipients/useOutlookEmailRecipients';
import type {RoamingSettingsState} from './RoamingSettingsContext';
import type {MessageAttachment} from './SendModal/EmailSync/structs';

export const SEND_DIALOG_PARENT_TOKEN_PARAM = 'parentDialogToken';
export const SEND_DIALOG_ATTACHMENT_SYNC_INTERVAL_MS = 1500;

export type ComposeMessageMetadata = {
  fromAddress: string;
  subject: string;
  recipients: OutlookRecipient[];
};

export type RoamingSettingsUpdate = {
  emailSyncOnSend?: boolean;
  podUrl?: string;
  refreshToken?: string;
  clearJwtAndRefreshTokens?: boolean;
};

export type DialogMessagePayload = Record<string, unknown> & {
  parentDialogToken?: string;
};

type ParentAttachmentsMessage = {
  type: 'attachments';
  attachments: MessageAttachment[];
};

type EmailSyncHeaderSetMessage = {
  type: 'emailSyncHeaderSet';
  ok: boolean;
  error?: string;
};

type ParentRoamingSettingsMessage = {
  type: 'roamingSettings';
  settings: RoamingSettingsState;
};

type RoamingSettingsUpdatedMessage = {
  type: 'roamingSettingsUpdated';
  ok: boolean;
  settings?: RoamingSettingsState;
  error?: string;
};

type ParentComposeMessageMetadataMessage = {
  type: 'composeMessageMetadata';
  metadata: ComposeMessageMetadata;
};

export const createDialogSessionToken = () => {
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }
  return `${Date.now()}:${Math.random()}`;
};

export const sendReviewDialogUrl = (token: string): string => {
  const url = new URL('/send-dialog.html', window.location.origin);
  const hashParams = new URLSearchParams();
  hashParams.set(SEND_DIALOG_PARENT_TOKEN_PARAM, token);
  url.hash = hashParams.toString();
  return url.toString();
};

export const getParentDialogToken = (): string | undefined => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get(SEND_DIALOG_PARENT_TOKEN_PARAM)?.trim();
  return token || undefined;
};

const parseJsonRecord = (raw: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

export const stringifyDialogMessage = (payload: DialogMessagePayload): string =>
  JSON.stringify(payload);

export const parseDialogMessage = (raw: string): DialogMessagePayload | null =>
  parseJsonRecord(raw);

const isMessageAttachment = (value: unknown): value is MessageAttachment => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const attachment = value as MessageAttachment;
  return typeof attachment.name === 'string' && attachment.name.trim() !== '';
};

const isOutlookRecipient = (value: unknown): value is OutlookRecipient => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const recipient = value as OutlookRecipient;
  return (
    typeof recipient.email === 'string' &&
    (recipient.field === 'to' ||
      recipient.field === 'cc' ||
      recipient.field === 'bcc')
  );
};

export const isComposeMessageMetadata = (
  value: unknown
): value is ComposeMessageMetadata => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const metadata = value as ComposeMessageMetadata;
  return (
    typeof metadata.fromAddress === 'string' &&
    typeof metadata.subject === 'string' &&
    Array.isArray(metadata.recipients) &&
    metadata.recipients.every(isOutlookRecipient)
  );
};

const isRoamingSettingsState = (
  value: unknown
): value is RoamingSettingsState => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const settings = value as RoamingSettingsState;
  return typeof settings.emailSyncOnSend === 'boolean';
};

export const parseParentAttachmentsMessage = (
  raw: string
): MessageAttachment[] | null => {
  const message = parseJsonRecord(raw) as ParentAttachmentsMessage | null;
  if (message?.type !== 'attachments' || !Array.isArray(message.attachments)) {
    return null;
  }
  return message.attachments.filter(isMessageAttachment);
};

export const parseEmailSyncHeaderSetMessage = (
  raw: string
): EmailSyncHeaderSetMessage | null => {
  const message = parseJsonRecord(raw) as EmailSyncHeaderSetMessage | null;
  return message?.type === 'emailSyncHeaderSet' ? message : null;
};

export const parseParentRoamingSettingsMessage = (
  raw: string
): RoamingSettingsState | null => {
  const message = parseJsonRecord(raw) as ParentRoamingSettingsMessage | null;
  if (
    message?.type !== 'roamingSettings' ||
    !isRoamingSettingsState(message.settings)
  ) {
    return null;
  }
  return message.settings;
};

export const parseParentComposeMessageMetadataMessage = (
  raw: string
): ComposeMessageMetadata | null => {
  const message = parseJsonRecord(
    raw
  ) as ParentComposeMessageMetadataMessage | null;
  if (
    message?.type !== 'composeMessageMetadata' ||
    !isComposeMessageMetadata(message.metadata)
  ) {
    return null;
  }
  return message.metadata;
};

export const parseRoamingSettingsUpdatedMessage = (
  raw: string
): RoamingSettingsUpdatedMessage | null => {
  const message = parseJsonRecord(raw) as RoamingSettingsUpdatedMessage | null;
  return message?.type === 'roamingSettingsUpdated' ? message : null;
};
